import { useEffect, useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import userInfo from '@recoil/userInfo.atoms';
import { AuthUserApi } from '@api/index';
import type { AccessibleByType } from '@TS/routesType';
import type { LoginParameter } from '@TS/auth';
import ShareLogin from '@function/shareLogin';
import { getUserInfo } from '@function/auth/userInfo';

type WithAuthProps = {
  accessibleBy: AccessibleByType;
  children: JSX.Element;
};

const WithAuth = ({ children, accessibleBy }: WithAuthProps): JSX.Element => {
  const setAuth = useSetRecoilState(userInfo);
  const navigate = useNavigate();

  const autoLoginFromPC = useCallback(
    async (userFromPC: string) => {
      const userInfo = userFromPC.split(' ');
      const loginRequest: LoginParameter = {
        username: userInfo[0],
        password: userInfo[1],
      };

      const userAuth = await AuthUserApi.login(loginRequest);
      setAuth(userAuth);
    },
    [setAuth, accessibleBy, navigate]
  );

  useEffect(() => {
    const user = getUserInfo();
    if (!user?.id) {
      const userFromPC = ShareLogin.getUserInfoFromPCVersion();
      if (userFromPC) autoLoginFromPC(userFromPC);
      else if (accessibleBy === 'authorized') {
        return navigate('/login');
      }
    } else if (accessibleBy === 'unauthorized') {
      return navigate('/');
    }
  }, [accessibleBy, autoLoginFromPC, navigate]);

  return children;
};

export default WithAuth;
